/**
 * Copyright (c) Streamlit Inc. (2018-2022) Snowflake Inc. (2022-2026)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { JSX } from "react"

import { Helmet } from "react-helmet-async"

import type { FontSources as FontSourcesType } from "@streamlit/app/src/util/useThemeManager"

interface FontSourcesProps {
  fontSources: FontSourcesType
}

/**
 * FontSources component manages font source link tags in the document head
 * using react-helmet-async for declarative head element management.
 *
 * @param fontSources - Map of configName to sourceUrl for font stylesheets
 */
const FontSources = ({ fontSources }: FontSourcesProps): JSX.Element => {
  return (
    <Helmet>
      {Object.entries(fontSources).map(([configName, sourceUrl]) => (
        <link
          key={configName}
          id={configName}
          rel="stylesheet"
          href={sourceUrl}
        />
      ))}
    </Helmet>
  )
}

export default FontSources
